import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { GlobalConfiguration } from '../models/GlobalConfiguration';
import { globalConfigurationCache } from 'GlobalConfigurationCache';

// Context value interface
interface GlobalConfigContextType {
  config: GlobalConfiguration | null;
  loading: boolean;
  refreshConfig: () => Promise<void>;
}

// Create the context
const GlobalConfigContext = createContext<GlobalConfigContextType | undefined>(undefined);

interface GlobalConfigProviderProps {
  children: ReactNode;
}

// Provider component
export const GlobalConfigProvider: React.FC<GlobalConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<GlobalConfiguration | null>(null);
  const [loading, setLoading] = useState(true);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const cfg = await globalConfigurationCache.getConfig();
      setConfig(cfg);
    } catch (error) {
      console.error('Failed to load global configuration:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshConfig = useCallback(async () => {
    globalConfigurationCache.clearCache();
    await loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    loadConfig();

    // Listen for cross-tab config updates
    const handleCrossTabUpdate = () => {
      console.log('Global config updated in another tab, refreshing...');
      refreshConfig();
    };

    // Modern browsers: BroadcastChannel
    let broadcastChannel: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        broadcastChannel = new BroadcastChannel('global-config-updates');
        broadcastChannel.addEventListener('message', (event) => {
          if (event.data?.type === 'CONFIG_UPDATED') {
            handleCrossTabUpdate();
          }
        });
      } catch (error) {
        console.warn('BroadcastChannel not available:', error);
      }
    }

    // Fallback: localStorage events (for older browsers)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'global-config-updated') {
        handleCrossTabUpdate();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      if (broadcastChannel) {
        broadcastChannel.close();
      }
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [loadConfig, refreshConfig]);

  return (
    <GlobalConfigContext.Provider value={{ config, loading, refreshConfig }}>
      {children}
    </GlobalConfigContext.Provider>
  );
};

// Hook to consume the context
export const useGlobalConfig = (): GlobalConfigContextType => {
  const ctx = useContext(GlobalConfigContext);
  if (!ctx) {
    throw new Error('useGlobalConfig must be used within a GlobalConfigProvider');
  }
  return ctx;
};
